import { useQueryClient } from "@tanstack/react-query"
import { Link, useSearch } from "@tanstack/react-router"
import { Lock, NotebookTabs, RefreshCw, Rocket } from "lucide-react"
import { type ReactNode, useEffect, useMemo, useState } from "react"
import { Bar, BarChart, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts"
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { DataFrame, Stat } from "@/data/frame"
import type { Scope } from "@/lib/connection"
import { useFormatNumber, useT } from "@/lib/i18n"
import { useScope } from "@/lib/scope"
import { useCell } from "@/lib/session"
import { usePrincipal } from "@/widget/runtime"
import { useOntology } from "@/workspace/hooks"
import { RefusalNote } from "@/workspace/values"
import { Cell } from "../cell"
import { type Decision, type Obj, toDecision, useAllObjects } from "../data"
import { useWorkbookDates } from "../dates"
import { type MappedFrom, mappedFrom } from "../lineage"
import { PromoteDialog } from "../promote-dialog"
import { type DecisionConfig, DecisionQueue, Row, Side } from "../review"
import { Sheet } from "../sheet"
import { PAYMENT_DECIDE_ACTION, PAY_TYPES, fromBank, fromInvoice, fromJournal, fromKashier, fromPaymentEntry, fromPaymob, fromSettlement, fromStore, kashierBatches } from "./data"
import {
  type BatchFinding,
  CONTRACTS,
  type Finding,
  type Payment,
  type Reason,
  type TxnFinding,
  decisionKey,
  needsReview,
  reconcileBatches,
  reconcileTransactions,
  summarize,
  weeklyFees,
  weeklyUnreconciled,
} from "./match"
import { PAY_REASON_COLOR, PayReasonPill, findingLabel, useFindingWhy, useMoney } from "./parts"
import { KASHIER_SYNC, PAYMOB_SYNC } from "./promote"
import { ConfidenceBar } from "../parts"

type TxnFilter = "all" | "review" | Reason
type LedgerTab = "invoices" | "payments" | "journals"

/** Every authorized record of the workbook's types, each read once and shared by its cells. */
function usePaymentReads(scope: Scope, has: (type: string) => boolean) {
  const installed = has(PAY_TYPES.paymob) && has(PAY_TYPES.kashier) && has(PAY_TYPES.invoice)
  const reads = {
    paymob: useAllObjects(scope, PAY_TYPES.paymob, installed),
    settlement: useAllObjects(scope, PAY_TYPES.settlement, installed && has(PAY_TYPES.settlement)),
    kashier: useAllObjects(scope, PAY_TYPES.kashier, installed),
    bank: useAllObjects(scope, PAY_TYPES.bank, installed && has(PAY_TYPES.bank)),
    store: useAllObjects(scope, PAY_TYPES.store, installed && has(PAY_TYPES.store)),
    invoice: useAllObjects(scope, PAY_TYPES.invoice, installed),
    payment: useAllObjects(scope, PAY_TYPES.payment, installed && has(PAY_TYPES.payment)),
    journal: useAllObjects(scope, PAY_TYPES.journal, installed && has(PAY_TYPES.journal)),
    decision: useAllObjects(scope, PAY_TYPES.decision, installed && has(PAY_TYPES.decision)),
  }
  const all = Object.values(reads)
  // The match runs only over complete reads: a refused or capped sheet would invent findings.
  const complete = installed && all.every((r) => !r.isError && (r.fetchStatus === "idle" && r.isPending ? true : r.isSuccess && !r.data?.truncated))
  const loading = all.some((r) => r.isPending && r.fetchStatus !== "idle")
  const truncated = all.some((r) => r.data?.truncated)
  return { installed, reads, complete: complete && !loading, loading, truncated, fetching: all.some((r) => r.isFetching) }
}

const items = (data: { items: Obj[] } | undefined) => data?.items ?? []

export function PaymentsWorkbookPage() {
  const t = useT()
  const scope = useScope()
  const search = useSearch({ strict: false }) as { focus?: string }
  const { ontology, defs } = useOntology(scope)
  const principal = usePrincipal(scope)
  const actor = principal.status === "ready" ? principal.actor : null
  const has = (type: string) => Boolean(ontology?.types.has(type))
  const { installed, reads, complete, loading, truncated, fetching } = usePaymentReads(scope, has)
  const queryClient = useQueryClient()
  const { connection } = useCell()
  const formatNumber = useFormatNumber()
  const money = useMoney()
  const dates = useWorkbookDates()
  const why = useFindingWhy()
  const [filter, setFilter] = useState<TxnFilter>("all")
  const [ledgerTab, setLedgerTab] = useState<LedgerTab>("payments")
  const [promoting, setPromoting] = useState(false)

  const data = {
    paymob: reads.paymob.data,
    settlement: reads.settlement.data,
    kashier: reads.kashier.data,
    bank: reads.bank.data,
    store: reads.store.data,
    invoice: reads.invoice.data,
    payment: reads.payment.data,
    journal: reads.journal.data,
    decision: reads.decision.data,
  }
  const paymob = useMemo(() => items(data.paymob).map(fromPaymob), [data.paymob])
  const kashier = useMemo(() => items(data.kashier).map(fromKashier), [data.kashier])
  const movements = useMemo(() => [...paymob, ...kashier].sort((a, b) => (a.day ?? "").localeCompare(b.day ?? "") || a.at.localeCompare(b.at)), [paymob, kashier])
  const batches = useMemo(() => [...items(data.settlement).map(fromSettlement), ...kashierBatches(items(data.kashier))], [data.settlement, data.kashier])
  const bank = useMemo(() => items(data.bank).map(fromBank), [data.bank])
  const ledger = useMemo(
    () => ({ stores: items(data.store).map(fromStore), invoices: items(data.invoice).map(fromInvoice), payments: items(data.payment).map(fromPaymentEntry), journals: items(data.journal).map(fromJournal) }),
    [data.store, data.invoice, data.payment, data.journal],
  )
  const today = new Date().toISOString().slice(0, 10)
  const txns = useMemo(() => (complete ? reconcileTransactions(movements, ledger) : []), [complete, movements, ledger])
  const batchFindings = useMemo(() => (complete ? reconcileBatches(batches, movements, bank, ledger.journals, today) : []), [complete, batches, movements, bank, ledger.journals, today])
  const summary = useMemo(() => summarize(txns, batchFindings), [txns, batchFindings])
  const decisions = useMemo(() => {
    const map = new Map<string, Decision>()
    for (const o of items(data.decision)) {
      const d = toDecision(o)
      if (d) map.set(d.id, d)
    }
    return map
  }, [data.decision])
  const review = useMemo<Finding[]>(() => [...batchFindings, ...txns].filter(needsReview).sort((a, b) => a.confidence - b.confidence || a.id.localeCompare(b.id)), [txns, batchFindings])
  const open = review.filter((f) => !decisions.has(decisionKey(f))).length
  const fees = useMemo(() => weeklyFees(txns), [txns])
  const unreconciled = useMemo(() => weeklyUnreconciled(txns), [txns])
  const feesHidden = movements.some((p) => p.feeHidden)
  const lineage = (type: string) => (defs.data ? mappedFrom(defs.data.artifacts, type) : null)
  const unreadable = movements.filter((p) => p.gross === null || p.day === null).length

  useEffect(() => {
    if (search.focus === "review" && !loading) document.getElementById("cell-pay-review")?.scrollIntoView({ block: "start" })
  }, [search.focus, loading])

  const counts = (provider: "Paymob" | "Kashier") => {
    const own = <T extends Finding>(f: T) => (f.subject === "batch" ? f.batch.provider : f.payment.provider) === provider
    const all = [...txns, ...batchFindings].filter(own)
    return {
      matched: all.filter((f) => f.reason === "posted" || f.reason === "refund_booked" || f.reason === "deposited").length,
      needsReview: all.filter((f) => needsReview(f) && !decisions.has(decisionKey(f))).length,
      unmatched: all.filter((f) => f.reason === "to_post").length,
    }
  }
  const proposals = {
    entries: txns.filter((f) => f.reason === "to_post"),
    feeBatches: batchFindings.filter((f) => !f.feeJournal && f.batch.transferred),
  }
  const rules = t("pw.rules.text", { paymob: `${CONTRACTS.Paymob.rate * 100}% + ${CONTRACTS.Paymob.fixed}`, kashier: `${CONTRACTS.Kashier.rate * 100}% + ${CONTRACTS.Kashier.fixed}` })
  const shownTxns = (filter === "all" ? txns : filter === "review" ? txns.filter(needsReview) : txns.filter((f) => f.reason === filter)).slice().sort((a, b) => Number(needsReview(b)) - Number(needsReview(a)) || a.confidence - b.confidence)

  const config = useMemo<DecisionConfig<Finding>>(
    () => ({
      action: PAYMENT_DECIDE_ACTION,
      idOf: (f) => f.id,
      keyOf: decisionKey,
      inputOf: (f, outcome) => ({
        decision_id: decisionKey(f),
        subject: f.subject,
        provider: f.subject === "batch" ? f.batch.provider : f.payment.provider,
        provider_ref: f.subject === "batch" ? f.batch.id : f.payment.id,
        ...(f.erp ? { erp_ref: f.erp } : {}),
        outcome,
        reason: f.reason,
        confidence: f.confidence,
      }),
      labelOf: findingLabel,
      markOf: (f) => (
        <>
          <PayReasonPill reason={f.reason} />
          <ConfidenceBar value={f.confidence} />
        </>
      ),
      detailOf: (f) => <FindingDetail scope={scope} finding={f} />,
      acceptLabel: t("pw.review.accept"),
      queueLabel: t("pw.review.queue"),
      emptyLabel: t("pw.review.empty"),
    }),
    [scope, t],
  )

  const from = (l: MappedFrom | null, property: string, fallback: string) => (l?.expressions.get(property) ? `${l.system} · ${l.dataset} · ${property}` : fallback)
  const chain = (l: MappedFrom | null, system: string, dataset: string, type: string, title: string) => [`${l?.system ?? system} · ${l?.dataset ?? dataset}`, l?.mapping ?? "", type, title].filter(Boolean)
  const hidden = (value: string | null, isHidden: boolean) => (isHidden ? t("wb.hidden") : value)

  const crumbs = [
    <Link key="wb" to="/$orgId/$environmentId/data/workbooks" params={scope} search={{}}>
      {t("wb.nav")}
    </Link>,
    t("pw.title"),
  ]

  return (
    <DataFrame
      title={t("pw.title")}
      description={t("pw.description")}
      how={["wb.how"]}
      width="full"
      crumbs={crumbs}
      actions={
        <>
          <button
            type="button"
            onClick={() => void queryClient.invalidateQueries({ queryKey: ["wb-all", connection.baseUrl] })}
            disabled={fetching}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border px-3 text-xs outline-none hover:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/30 disabled:opacity-60"
          >
            <RefreshCw className={fetching ? "size-3.5 animate-spin" : "size-3.5"} aria-hidden /> {t("wb.run")}
          </button>
          <button
            type="button"
            onClick={() => setPromoting(true)}
            disabled={!installed || !complete}
            className="inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground px-3 text-xs font-medium text-background outline-none hover:opacity-90 focus-visible:ring-3 focus-visible:ring-ring/30 disabled:opacity-50"
          >
            <Rocket className="size-3.5" aria-hidden /> {t("wb.promote.button")}
          </button>
        </>
      }
    >
      {defs.isError ? (
        <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
      ) : !ontology ? (
        <TableSkeleton rows={6} columns={5} />
      ) : !installed ? (
        <EmptyState icon={<NotebookTabs />} title={t("pw.notInstalled")}>
          {t("pw.notInstalledBody")}
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 border-y border-border py-4 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label={t("pw.stat.transactions")} value={loading ? "…" : formatNumber(movements.length)} hint={t("pw.stat.providers", { paymob: formatNumber(paymob.length), kashier: formatNumber(kashier.length) })} />
            <Stat label={t("pw.stat.posted")} value={loading ? "…" : formatNumber(summary.posted)} />
            <Stat label={t("pw.stat.toPost")} value={loading ? "…" : formatNumber(summary.toPost)} hint={`EGP ${money(summary.toPostAmount)}`} />
            <Stat label={t("wb.stat.review")} value={loading ? "…" : formatNumber(open)} hint={t("pw.stat.decided", { n: formatNumber(review.length - open) })} />
            <Stat label={t("pw.stat.unreconciled")} value={loading ? "…" : money(summary.unreconciled)} hint="EGP" />
            <Stat
              label={t("pw.stat.fees")}
              value={loading ? "…" : summary.fees === null ? <HiddenFees /> : money(summary.fees)}
              hint={summary.fees === null ? t("pw.lens.financeOnly") : t("pw.stat.feesHint")}
            />
          </div>

          {feesHidden ? (
            <p className="flex items-center gap-2 rounded-xl border border-dashed border-border px-4 py-2.5 text-xs text-muted-foreground">
              <Lock className="size-3.5 shrink-0" aria-hidden /> {t("pw.lens.note")}
            </p>
          ) : null}
          {installed && !loading && !complete ? (
            <p role="alert" className="rounded-xl border border-dashed border-destructive/50 px-4 py-3 text-sm">
              {truncated ? t("wb.incomplete.truncated") : t("wb.incomplete.refused")}
            </p>
          ) : null}

          <div className="pt-2">
            <Cell
              index={1}
              kind="source"
              title={t("pw.cell.paymob")}
              summary={reads.paymob.data ? t("wb.sheet.rows", { count: paymob.length }) : undefined}
              chain={chain(lineage(PAY_TYPES.paymob), "Paymob", "transactions", PAY_TYPES.paymob, t("pw.cell.paymob"))}
              lineage={[
                { column: t("pw.col.transaction"), from: from(lineage(PAY_TYPES.paymob), "row_id", "transactions.id"), means: t("pw.mean.paymobId") },
                { column: t("pw.col.type"), from: "transactions.type", means: t("pw.mean.type") },
                { column: t("pw.col.storeOrder"), from: "transactions.merchant_order_id", means: t("pw.mean.storeOrder") },
                { column: "amount_cents", from: "transactions.amount_cents", means: t("pw.mean.amountCents") },
                { column: t("pw.col.instrument"), from: "transactions.source_data_sub_type · source_data_pan", means: t("pw.mean.instrument") },
                { column: "merchant_commission · vat", from: "transactions.merchant_commission · transactions.vat", means: t("pw.mean.fee") },
                { column: t("pw.col.batch"), from: "transactions.settlement_id", means: t("pw.mean.settlement") },
              ]}
            >
              <SourceState read={reads.paymob}>
                <Sheet
                  label={t("pw.cell.paymob")}
                  rows={paymob}
                  rowKey={(r) => r.key}
                  empty={t("pw.empty.source")}
                  columns={[
                    { id: "id", label: "id", value: (r) => r.id, mono: true, width: "7.5rem" },
                    { id: "at", label: "created_at", value: (r) => r.at, mono: true, width: "13.5rem" },
                    { id: "type", label: "type", value: (r) => (r.kind === "capture" ? "Payment" : r.kind === "refund" ? "Refund" : "Chargeback"), width: "7rem" },
                    { id: "order", label: "merchant_order_id", value: (r) => r.orderRef, mono: true, width: "9.5rem" },
                    { id: "amount", label: "amount_cents", value: (r) => r.amountText, numeric: true, width: "8rem" },
                    { id: "card", label: t("pw.col.instrument"), value: (r) => (r.instrumentHidden ? t("wb.hidden") : r.instrument), width: "11rem" },
                    { id: "fee", label: "merchant_commission", value: (r) => hidden(r.fee === null ? null : String(Math.round(r.fee * 100)), r.feeHidden), numeric: true, width: "9.5rem" },
                    { id: "vat", label: "vat", value: (r) => hidden(r.vat === null ? null : String(Math.round(r.vat * 100)), r.feeHidden), numeric: true, width: "6rem" },
                    { id: "batch", label: "settlement_id", value: (r) => r.batch, mono: true },
                  ]}
                />
              </SourceState>
            </Cell>

            <Cell
              index={2}
              kind="source"
              title={t("pw.cell.settlements")}
              summary={reads.settlement.data ? t("wb.sheet.rows", { count: items(data.settlement).length }) : undefined}
              chain={chain(lineage(PAY_TYPES.settlement), "Paymob", "settlements", PAY_TYPES.settlement, t("pw.cell.settlements"))}
              lineage={[
                { column: "settlement_id", from: "settlements.settlement_id", means: t("pw.mean.settlement") },
                { column: "net_amount", from: "settlements.net_amount", means: t("pw.mean.reportedNet") },
                { column: "bank_reference", from: "settlements.bank_reference", means: t("pw.mean.bankReference") },
              ]}
            >
              <SourceState read={reads.settlement}>
                <Sheet
                  label={t("pw.cell.settlements")}
                  rows={batches.filter((b) => b.provider === "Paymob")}
                  rowKey={(r) => r.key}
                  empty={t("pw.empty.source")}
                  columns={[
                    { id: "id", label: "settlement_id", value: (r) => r.id, mono: true, width: "8rem" },
                    { id: "date", label: "settlement_date", value: (r) => r.date, display: (r) => dates.day(r.date), width: "9rem" },
                    { id: "net", label: "net_amount", value: (r) => (r.netHidden ? t("wb.hidden") : r.reportedNet), display: (r) => (r.netHidden ? t("wb.hidden") : money(r.reportedNet)), numeric: true, width: "9rem" },
                    { id: "ref", label: "bank_reference", value: (r) => r.bankReference, mono: true, width: "11rem" },
                    { id: "status", label: "status", value: (r) => (r.transferred ? "Transferred" : "Pending") },
                  ]}
                />
              </SourceState>
            </Cell>

            <Cell
              index={3}
              kind="source"
              title={t("pw.cell.kashier")}
              summary={reads.kashier.data ? t("wb.sheet.rows", { count: kashier.length }) : undefined}
              chain={chain(lineage(PAY_TYPES.kashier), "Kashier", "payouts", PAY_TYPES.kashier, t("pw.cell.kashier"))}
              lineage={[
                { column: "transactionId", from: from(lineage(PAY_TYPES.kashier), "transaction_id", "payouts.transactionId"), means: t("pw.mean.kashierId") },
                { column: "event", from: "payouts.event", means: t("pw.mean.type") },
                { column: "merchantOrderId", from: "payouts.merchantOrderId", means: t("pw.mean.storeOrder") },
                { column: "grossAmount", from: "payouts.grossAmount", means: t("pw.mean.grossEgp") },
                { column: "fees · vat · netAmount", from: "payouts.fees · payouts.vat · payouts.netAmount", means: t("pw.mean.fee") },
                { column: "payoutId", from: "payouts.payoutId", means: t("pw.mean.payout") },
              ]}
            >
              <SourceState read={reads.kashier}>
                <Sheet
                  label={t("pw.cell.kashier")}
                  rows={kashier}
                  rowKey={(r) => r.key}
                  empty={t("pw.empty.source")}
                  columns={[
                    { id: "payout", label: "payoutId", value: (r) => r.batch, mono: true, width: "9.5rem" },
                    { id: "id", label: "transactionId", value: (r) => r.id, mono: true, width: "8.5rem" },
                    { id: "at", label: "transactionDate", value: (r) => r.at, mono: true, width: "13rem" },
                    { id: "event", label: "event", value: (r) => (r.kind === "capture" ? "pay" : "refund"), width: "6rem" },
                    { id: "order", label: "merchantOrderId", value: (r) => r.orderRef, mono: true, width: "9rem" },
                    { id: "gross", label: "grossAmount", value: (r) => r.amountText, numeric: true, width: "8rem" },
                    { id: "fees", label: "fees", value: (r) => hidden(r.fee === null ? null : money(r.fee), r.feeHidden), numeric: true, width: "6rem" },
                    { id: "card", label: t("pw.col.instrument"), value: (r) => (r.instrumentHidden ? t("wb.hidden") : r.instrument) },
                  ]}
                />
              </SourceState>
            </Cell>

            <Cell
              index={4}
              kind="source"
              title={t("pw.cell.bank")}
              summary={reads.bank.data ? t("wb.sheet.rows", { count: bank.length }) : undefined}
              chain={chain(lineage(PAY_TYPES.bank), "CIB", "statement", PAY_TYPES.bank, t("pw.cell.bank"))}
              lineage={[
                { column: "reference", from: "statement.reference", means: t("pw.mean.bankLine") },
                { column: "credit", from: "statement.credit", means: t("pw.mean.credit") },
              ]}
            >
              <SourceState read={reads.bank}>
                <Sheet
                  label={t("pw.cell.bank")}
                  rows={bank}
                  rowKey={(r) => r.key}
                  empty={t("pw.empty.source")}
                  columns={[
                    { id: "ref", label: "reference", value: (r) => r.reference, mono: true, width: "10rem" },
                    { id: "date", label: "booking_date", value: (r) => r.date, display: (r) => dates.day(r.date), width: "8.5rem" },
                    { id: "desc", label: "description", value: (r) => r.description },
                    { id: "debit", label: "debit", value: (r) => (r.amountHidden ? t("wb.hidden") : r.debit), display: (r) => (r.amountHidden ? t("wb.hidden") : r.debit === null ? "" : money(r.debit)), numeric: true, width: "8.5rem" },
                    { id: "credit", label: "credit", value: (r) => (r.amountHidden ? t("wb.hidden") : r.credit), display: (r) => (r.amountHidden ? t("wb.hidden") : r.credit === null ? "" : money(r.credit)), numeric: true, width: "8.5rem" },
                  ]}
                />
              </SourceState>
            </Cell>

            <Cell
              index={5}
              kind="source"
              title={t("pw.cell.ledger")}
              summary={t("pw.ledger.summary", { invoices: formatNumber(ledger.invoices.length), payments: formatNumber(ledger.payments.length), journals: formatNumber(ledger.journals.length) })}
              chain={chain(lineage(PAY_TYPES.payment), "ERPNext", "Payment Entry", PAY_TYPES.payment, t("pw.cell.ledger"))}
              lineage={[
                { column: "Sales Invoice", from: `${lineage(PAY_TYPES.invoice)?.mapping ?? "erpnext.ledger"} · Sales Invoice (+ items.sales_order)`, means: t("pw.mean.invoice") },
                { column: "Payment Entry", from: `${lineage(PAY_TYPES.payment)?.mapping ?? "erpnext.ledger"} · Payment Entry.reference_no (+ references)`, means: t("pw.mean.paymentEntry") },
                { column: "Journal Entry", from: `${lineage(PAY_TYPES.journal)?.mapping ?? "erpnext.ledger"} · Journal Entry.cheque_no`, means: t("pw.mean.journal") },
              ]}
            >
              <div role="tablist" aria-label={t("pw.cell.ledger")} className="flex flex-wrap gap-1.5">
                {(["payments", "invoices", "journals"] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={ledgerTab === tab}
                    onClick={() => setLedgerTab(tab)}
                    className={`inline-flex h-7 items-center rounded-full border px-2.5 text-xs outline-none focus-visible:ring-3 focus-visible:ring-ring/30 ${ledgerTab === tab ? "border-foreground/40 bg-muted" : "border-border hover:bg-muted/50"}`}
                  >
                    {t(`pw.ledger.${tab}` as const)}
                  </button>
                ))}
              </div>
              {ledgerTab === "invoices" ? (
                <SourceState read={reads.invoice}>
                  <Sheet
                    label={t("pw.ledger.invoices")}
                    rows={ledger.invoices}
                    rowKey={(r) => r.name}
                    empty={t("pw.empty.source")}
                    columns={[
                      { id: "name", label: "name", value: (r) => r.name, mono: true, width: "13rem" },
                      { id: "date", label: "posting_date", value: (r) => r.date, display: (r) => dates.day(r.date), width: "8.5rem" },
                      { id: "total", label: "grand_total", value: (r) => r.total, display: (r) => money(r.total), numeric: true, width: "8.5rem" },
                      { id: "out", label: "outstanding_amount", value: (r) => r.outstanding, display: (r) => money(r.outstanding), numeric: true, width: "9.5rem" },
                      { id: "ret", label: "return_against", value: (r) => (r.isReturn ? r.returnAgainst : null), mono: true, width: "13rem" },
                      { id: "so", label: "sales_order", value: (r) => r.salesOrder, mono: true },
                    ]}
                  />
                </SourceState>
              ) : ledgerTab === "payments" ? (
                <SourceState read={reads.payment}>
                  <Sheet
                    label={t("pw.ledger.payments")}
                    rows={ledger.payments}
                    rowKey={(r) => r.name}
                    empty={t("pw.empty.source")}
                    columns={[
                      { id: "name", label: "name", value: (r) => r.name, mono: true, width: "13rem" },
                      { id: "date", label: "posting_date", value: (r) => r.date, display: (r) => dates.day(r.date), width: "8.5rem" },
                      { id: "mode", label: "mode_of_payment", value: (r) => r.mode, width: "9rem" },
                      { id: "ref", label: "reference_no", value: (r) => r.referenceNo, mono: true, width: "13rem" },
                      { id: "amount", label: "received_amount", value: (r) => r.amount, display: (r) => money(r.amount), numeric: true, width: "9rem" },
                      { id: "inv", label: "Sales Invoice", value: (r) => r.invoice, mono: true },
                    ]}
                  />
                </SourceState>
              ) : (
                <SourceState read={reads.journal}>
                  <Sheet
                    label={t("pw.ledger.journals")}
                    rows={ledger.journals}
                    rowKey={(r) => r.name}
                    empty={t("pw.empty.source")}
                    columns={[
                      { id: "name", label: "name", value: (r) => r.name, mono: true, width: "13rem" },
                      { id: "type", label: "voucher_type", value: (r) => r.voucherType, width: "9rem" },
                      { id: "date", label: "posting_date", value: (r) => r.date, display: (r) => dates.day(r.date), width: "8.5rem" },
                      { id: "cheque", label: "cheque_no", value: (r) => r.chequeNo, mono: true, width: "12rem" },
                      { id: "total", label: "total_debit", value: (r) => (r.total === null ? t("wb.hidden") : r.total), display: (r) => (r.total === null ? t("wb.hidden") : money(r.total)), numeric: true },
                    ]}
                  />
                </SourceState>
              )}
            </Cell>

            <Cell
              index={6}
              kind="transform"
              title={t("pw.cell.normalize")}
              summary={unreadable ? t("pw.normalize.unreadable", { n: formatNumber(unreadable) }) : t("wb.normalize.allRead")}
              chain={[t("pw.cell.paymob"), t("pw.cell.kashier"), t("pw.cell.normalize")]}
              lineage={[
                { column: t("pw.col.gross"), from: "Paymob: amount_cents ÷ 100 (− for Refund, Chargeback) · Kashier: grossAmount", means: t("pw.mean.gross") },
                { column: t("pw.col.fee"), from: "Paymob: merchant_commission ÷ 100 · Kashier: fees", means: t("pw.mean.fee") },
                { column: t("pw.col.vat"), from: "Paymob: vat ÷ 100 · Kashier: vat", means: t("pw.mean.vat") },
                { column: t("wb.col.day"), from: "Paymob: date of created_at (Cairo) · Kashier: transactionDate (UTC) + 3 h", means: t("pw.mean.day") },
                { column: t("pw.col.batch"), from: "Paymob: settlement_id · Kashier: payoutId", means: t("pw.mean.batch") },
              ]}
            >
              <p className="max-w-3xl text-xs text-muted-foreground">{t("pw.normalize.body")}</p>
              <Sheet
                label={t("pw.cell.normalize")}
                rows={movements}
                rowKey={(r) => r.key}
                empty={t("pw.empty.source")}
                rowTone={(r) => (r.gross === null || r.day === null ? "bad" : undefined)}
                columns={[
                  { id: "provider", label: t("pw.col.provider"), value: (r) => r.provider, width: "6.5rem" },
                  { id: "id", label: t("pw.col.transaction"), value: (r) => r.id, mono: true, width: "8.5rem" },
                  { id: "kind", label: t("pw.col.type"), value: (r) => t(`pw.kind.${r.kind}` as const), width: "7rem" },
                  { id: "day", label: t("wb.col.day"), value: (r) => r.day, display: (r) => dates.day(r.day), width: "8rem" },
                  { id: "order", label: t("pw.col.storeOrder"), value: (r) => r.orderRef, mono: true, width: "8.5rem" },
                  { id: "gross", label: t("pw.col.gross"), value: (r) => r.gross, display: (r) => money(r.gross), numeric: true, width: "8rem" },
                  { id: "fee", label: t("pw.col.fee"), value: (r) => (r.feeHidden ? t("wb.hidden") : r.fee), display: (r) => (r.feeHidden ? t("wb.hidden") : money(r.fee)), numeric: true, width: "7rem" },
                  { id: "vat", label: t("pw.col.vat"), value: (r) => (r.feeHidden ? t("wb.hidden") : r.vat), display: (r) => (r.feeHidden ? t("wb.hidden") : money(r.vat)), numeric: true, width: "6.5rem" },
                  { id: "net", label: t("pw.col.net"), value: (r) => (r.feeHidden ? t("wb.hidden") : r.net), display: (r) => (r.feeHidden ? t("wb.hidden") : money(r.net)), numeric: true, width: "8rem" },
                  { id: "batch", label: t("pw.col.batch"), value: (r) => r.batch, mono: true },
                ]}
              />
            </Cell>

            <Cell
              index={7}
              kind="match"
              title={t("pw.cell.matchTxns")}
              summary={t("pw.match.summary", { n: formatNumber(txns.length), review: formatNumber(txns.filter(needsReview).length), toPost: formatNumber(summary.toPost) })}
              chain={[t("pw.cell.normalize"), t("pw.cell.ledger"), t("pw.cell.matchTxns")]}
              lineage={[
                { column: t("wb.col.verdict"), from: "rules 1–5 (this cell)", means: t("pw.mean.verdict") },
                { column: t("wb.col.confidence"), from: "rule × agreement", means: t("pw.mean.confidence") },
                { column: t("pw.col.erp"), from: "Payment Entry.reference_no = id · else Sales Invoice of Shopify.erp_reference", means: t("pw.mean.erp") },
                { column: t("pw.col.delta"), from: "gross − Payment Entry / invoice outstanding · fee + VAT − contract", means: t("pw.mean.delta") },
              ]}
            >
              <ol className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2 lg:grid-cols-4">
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">1 · {t("pw.rule.posted")}</span> {t("pw.rule.postedBody")}
                </li>
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">2 · {t("pw.rule.invoice")}</span> {t("pw.rule.invoiceBody")}
                </li>
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">3 · {t("pw.rule.fee")}</span> {t("pw.rule.feeBody", { paymob: `${CONTRACTS.Paymob.rate * 100}% + EGP ${CONTRACTS.Paymob.fixed}`, kashier: `${CONTRACTS.Kashier.rate * 100}% + EGP ${CONTRACTS.Kashier.fixed}` })}
                </li>
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">4 · {t("pw.rule.refund")}</span> {t("pw.rule.refundBody")}
                </li>
              </ol>
              <div role="group" aria-label={t("wb.match.filter")} className="flex flex-wrap gap-1.5">
                {(
                  [
                    ["all", txns.length],
                    ["review", txns.filter(needsReview).length],
                    ...(["posted", "to_post", "refund_booked", "fee_mismatch", "duplicate_capture", "refund_no_credit_note", "chargeback", "already_paid", "no_invoice"] as const).map((r) => [r, summary.byReason[r]] as const),
                  ] as const
                ).map(([id, count]) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={filter === id}
                    onClick={() => setFilter(id)}
                    className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs outline-none focus-visible:ring-3 focus-visible:ring-ring/30 ${filter === id ? "border-foreground/40 bg-muted" : "border-border hover:bg-muted/50"}`}
                  >
                    {id !== "all" && id !== "review" ? <span className="size-1.5 rounded-full" style={{ background: PAY_REASON_COLOR[id] }} aria-hidden /> : null}
                    {id === "all" ? t("wb.match.all") : id === "review" ? t("pw.filter.review") : t(`pw.reason.${id}` as const)}
                    <span className="tabular-nums text-muted-foreground">{formatNumber(count)}</span>
                  </button>
                ))}
              </div>
              {loading ? (
                <TableSkeleton rows={6} columns={6} />
              ) : (
                <Sheet
                  label={t("pw.cell.matchTxns")}
                  rows={shownTxns}
                  rowKey={(f) => f.id}
                  empty={t("wb.empty.match")}
                  rowTone={(f) => (f.reason === "to_post" ? undefined : needsReview(f) ? (f.confidence >= 90 ? "bad" : "warn") : undefined)}
                  columns={[
                    { id: "verdict", label: t("wb.col.verdict"), value: (f) => f.reason, render: (f: TxnFinding) => <PayReasonPill reason={f.reason} />, width: "10.5rem" },
                    { id: "conf", label: t("wb.col.confidence"), value: (f) => f.confidence, render: (f: TxnFinding) => <ConfidenceBar value={f.confidence} />, width: "6.5rem" },
                    { id: "txn", label: t("pw.col.transaction"), value: (f) => `${f.payment.provider} ${f.payment.id}`, width: "10rem" },
                    { id: "order", label: t("pw.col.storeOrder"), value: (f) => f.store?.name ?? f.payment.orderRef, width: "7rem" },
                    { id: "erp", label: t("pw.col.erp"), value: (f) => f.erp, mono: true, width: "12.5rem" },
                    { id: "gross", label: t("pw.col.gross"), value: (f) => f.payment.gross, display: (f) => money(f.payment.gross), numeric: true, width: "8rem" },
                    { id: "delta", label: t("pw.col.delta"), value: (f) => f.delta, display: (f) => (f.delta ? money(f.delta) : ""), numeric: true, width: "6rem" },
                    { id: "why", label: t("wb.col.why"), value: (f) => why(f) },
                  ]}
                />
              )}
            </Cell>

            <Cell
              index={8}
              kind="match"
              title={t("pw.cell.matchBatches")}
              summary={t("pw.batches.summary", { n: formatNumber(batchFindings.length), review: formatNumber(batchFindings.filter(needsReview).length) })}
              chain={[t("pw.cell.settlements"), t("pw.cell.kashier"), t("pw.cell.bank"), t("pw.cell.matchBatches")]}
              lineage={[
                { column: t("pw.col.lines"), from: "Σ net of the batch's transactions", means: t("pw.mean.lines") },
                { column: t("pw.col.reported"), from: "Paymob: settlements.net_amount · Kashier: payoutNetAmount", means: t("pw.mean.reportedNet") },
                { column: t("pw.col.deposit"), from: "statement.reference = bank_reference · else credit = net within ±2 days", means: t("pw.mean.deposit") },
                { column: t("pw.col.feeJournal"), from: "Journal Entry.cheque_no = batch id", means: t("pw.mean.feeJournal") },
              ]}
            >
              <ol className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">1 · {t("pw.rule.sum")}</span> {t("pw.rule.sumBody")}
                </li>
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">2 · {t("pw.rule.deposit")}</span> {t("pw.rule.depositBody")}
                </li>
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">3 · {t("pw.rule.missing")}</span> {t("pw.rule.missingBody")}
                </li>
              </ol>
              {loading ? (
                <TableSkeleton rows={6} columns={6} />
              ) : (
                <Sheet
                  label={t("pw.cell.matchBatches")}
                  rows={[...batchFindings].sort((a, b) => Number(needsReview(b)) - Number(needsReview(a)) || (a.batch.date ?? "").localeCompare(b.batch.date ?? ""))}
                  rowKey={(f) => f.id}
                  empty={t("wb.empty.match")}
                  rowTone={(f) => (needsReview(f) ? (f.confidence >= 90 ? "bad" : "warn") : undefined)}
                  columns={[
                    { id: "verdict", label: t("wb.col.verdict"), value: (f) => f.reason, render: (f: BatchFinding) => <PayReasonPill reason={f.reason} />, width: "10.5rem" },
                    { id: "batch", label: t("pw.col.batch"), value: (f) => `${f.batch.provider} ${f.batch.id}`, width: "11.5rem" },
                    { id: "date", label: t("wb.col.date"), value: (f) => f.batch.date, display: (f) => dates.day(f.batch.date), width: "8rem" },
                    { id: "lines", label: t("pw.col.lines"), value: (f) => (f.lineNet === null ? t("wb.hidden") : f.lineNet), display: (f) => (f.lineNet === null ? t("wb.hidden") : money(f.lineNet)), numeric: true, width: "8.5rem" },
                    { id: "reported", label: t("pw.col.reported"), value: (f) => (f.batch.netHidden ? t("wb.hidden") : f.batch.reportedNet), display: (f) => (f.batch.netHidden ? t("wb.hidden") : money(f.batch.reportedNet)), numeric: true, width: "8.5rem" },
                    { id: "deposit", label: t("pw.col.deposit"), value: (f) => f.deposit?.reference ?? null, mono: true, width: "10rem" },
                    { id: "je", label: t("pw.col.feeJournal"), value: (f) => f.feeJournal?.name ?? null, mono: true, width: "11rem" },
                    { id: "why", label: t("wb.col.why"), value: (f) => why(f) },
                  ]}
                />
              )}
            </Cell>

            <Cell
              id="cell-pay-review"
              index={9}
              kind="review"
              title={t("pw.cell.review")}
              summary={t("wb.review.summary", { open, decided: review.length - open })}
              chain={[t("pw.cell.matchTxns"), t("pw.cell.matchBatches"), t("pw.cell.review"), `${PAY_TYPES.decision} (ledger)`]}
              lineage={[
                { column: t("wb.col.verdict"), from: `${PAYMENT_DECIDE_ACTION} → ${PAY_TYPES.decision}.outcome`, means: t("wb.mean.decision") },
                { column: t("wb.review.receipt"), from: "ActionAccepted.receipt.seq", means: t("wb.mean.receipt") },
              ]}
            >
              {reads.decision.isError ? <RefusalNote error={reads.decision.error} title={t("wb.readRefused")} /> : null}
              {loading ? <TableSkeleton rows={5} columns={3} /> : complete ? <DecisionQueue scope={scope} items={review} decisions={decisions} actor={actor} config={config} /> : null}
            </Cell>

            <Cell
              index={10}
              kind="chart"
              title={t("pw.cell.feesChart")}
              summary={fees ? t("wb.chart.summary", { weeks: fees.length }) : t("pw.lens.financeOnly")}
              chain={[t("pw.cell.normalize"), t("pw.cell.feesChart")]}
              lineage={[{ column: t("pw.col.fee"), from: "Σ (fee + VAT) per provider per Monday of day", means: t("pw.mean.feesChart") }]}
            >
              {fees === null ? (
                <p className="flex items-center gap-2 rounded-xl border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
                  <Lock className="size-4 shrink-0" aria-hidden /> {t("pw.lens.chartHidden")}
                </p>
              ) : fees.length ? (
                <ChartContainer className="h-60">
                  <BarChart data={fees} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="var(--border)" />
                    <XAxis dataKey="week" tickLine={false} axisLine={false} fontSize={11} stroke="var(--muted-foreground)" tickFormatter={(week: string) => dates.day(week) ?? week} />
                    <YAxis tickLine={false} axisLine={false} fontSize={11} stroke="var(--muted-foreground)" />
                    <ChartTooltip cursor={{ fill: "var(--muted)" }} content={<ChartTooltipContent />} />
                    <Bar dataKey="Paymob" stackId="f" name="Paymob" fill="var(--chart-1)" radius={0} />
                    <Bar dataKey="Kashier" stackId="f" name="Kashier" fill="var(--chart-2)" radius={0} />
                  </BarChart>
                </ChartContainer>
              ) : (
                <p className="text-sm text-muted-foreground">{t("wb.empty.chart")}</p>
              )}
            </Cell>

            <Cell
              index={11}
              kind="chart"
              title={t("pw.cell.unreconciledChart")}
              summary={t("pw.unreconciled.summary", { amount: money(summary.unreconciled) })}
              chain={[t("pw.cell.matchTxns"), t("pw.cell.unreconciledChart")]}
              lineage={[{ column: "EGP", from: "Σ |gross| per Monday of day, not posted or booked", means: t("pw.mean.unreconciledChart") }]}
            >
              {unreconciled.length ? (
                <ChartContainer className="h-60">
                  <ComposedChart data={unreconciled} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="var(--border)" />
                    <XAxis dataKey="week" tickLine={false} axisLine={false} fontSize={11} stroke="var(--muted-foreground)" tickFormatter={(week: string) => dates.day(week) ?? week} />
                    <YAxis tickLine={false} axisLine={false} fontSize={11} stroke="var(--muted-foreground)" />
                    <ChartTooltip cursor={{ fill: "var(--muted)" }} content={<ChartTooltipContent />} />
                    <Bar dataKey="to_post" stackId="u" name={t("pw.reason.to_post")} fill={PAY_REASON_COLOR.to_post} radius={0} />
                    <Bar dataKey="review" stackId="u" name={t("pw.filter.review")} fill="var(--warning)" radius={0} />
                    <Line dataKey="cumulative" name={t("pw.chart.cumulative")} type="monotone" stroke="var(--foreground)" strokeWidth={1.5} dot={false} />
                  </ComposedChart>
                </ChartContainer>
              ) : (
                <p className="text-sm text-muted-foreground">{t("wb.empty.chart")}</p>
              )}
            </Cell>

            <Cell
              index={12}
              kind="promote"
              title={t("pw.cell.promote")}
              summary={t("pw.promote.summary", { entries: formatNumber(proposals.entries.length), journals: formatNumber(proposals.feeBatches.length) })}
              chain={[t("pw.cell.matchTxns"), t("pw.cell.matchBatches"), "recon.paymob_ledger · recon.kashier_ledger", "recon.OrderSync (proposed)"]}
              lineage={[
                { column: "Payment Entry", from: "recon.ProposedPaymentEntry ← transactions (Payment, success) · payouts (pay, SUCCESS)", means: t("pw.mean.proposedEntry") },
                { column: "Journal Entry", from: "recon.ProposedFeeLine ← settlements · payouts (fees, vat)", means: t("pw.mean.proposedJournal") },
              ]}
              actions={
                <button
                  type="button"
                  onClick={() => setPromoting(true)}
                  disabled={!complete}
                  className="inline-flex h-7 items-center gap-1.5 rounded-full bg-foreground px-3 text-xs font-medium text-background outline-none hover:opacity-90 focus-visible:ring-3 focus-visible:ring-ring/30 disabled:opacity-50"
                >
                  <Rocket className="size-3.5" aria-hidden /> {t("wb.promote.button")}
                </button>
              }
            >
              <p className="max-w-3xl text-xs text-muted-foreground">{t("pw.promote.body")}</p>
              <Sheet
                label={t("pw.cell.promote")}
                rows={proposals.entries}
                rowKey={(f) => f.id}
                empty={t("pw.promote.none")}
                columns={[
                  { id: "doc", label: t("pw.col.proposal"), value: () => "Payment Entry", width: "8.5rem" },
                  { id: "mode", label: "mode_of_payment", value: (f) => f.payment.provider, width: "8.5rem" },
                  { id: "ref", label: "reference_no", value: (f) => f.payment.id, mono: true, width: "10rem" },
                  { id: "date", label: "posting_date", value: (f) => f.payment.day, display: (f) => dates.day(f.payment.day), width: "8.5rem" },
                  { id: "amount", label: "received_amount", value: (f) => f.payment.gross, display: (f) => money(f.payment.gross), numeric: true, width: "9rem" },
                  { id: "inv", label: "Sales Invoice", value: (f) => f.erp, mono: true, width: "13rem" },
                  { id: "to", label: "paid_to", value: (f) => `${f.payment.provider} Clearing - NSR` },
                ]}
              />
            </Cell>
          </div>
          {promoting ? (
            <PromoteDialog
              scope={scope}
              actor={actor}
              syncs={[
                { spec: PAYMOB_SYNC, counts: counts("Paymob") },
                { spec: KASHIER_SYNC, counts: counts("Kashier") },
              ]}
              rules={rules}
              ready={complete}
              onClose={() => setPromoting(false)}
              title={t("pw.promote.title")}
              description={t("pw.promote.description")}
              footnote={t("pw.promote.install")}
            />
          ) : null}
        </>
      )}
    </DataFrame>
  )
}

function HiddenFees() {
  const t = useT()
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
      <Lock className="size-3.5" aria-hidden /> {t("wb.hidden")}
    </span>
  )
}

/** A source cell's read: its refusal, its skeleton, or its sheet. */
function SourceState({ read, children }: { read: { isError: boolean; error: unknown; isPending: boolean; fetchStatus: string }; children: ReactNode }) {
  const t = useT()
  if (read.isError) return <RefusalNote error={read.error} title={t("wb.readRefused")} />
  if (read.isPending && read.fetchStatus !== "idle") return <TableSkeleton rows={6} columns={6} />
  return <>{children}</>
}

/** A finding beside the queue: the provider's record on one side, the ledger's on the other. */
function FindingDetail({ scope, finding: f }: { scope: Scope; finding: Finding }) {
  const t = useT()
  const why = useFindingWhy()
  const money = useMoney()
  const dates = useWorkbookDates()
  const record = (objectType: string, key: string, label: string) => (
    <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType, key }} className="font-medium hover:underline">
      {label}
    </Link>
  )
  if (f.subject === "batch") {
    const b = f.batch
    return (
      <>
        <p className="text-sm">{why(f)}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Side title={b.provider}>
            <Row label={t("pw.col.batch")}>{b.provider === "Paymob" ? record(PAY_TYPES.settlement, b.id, b.id) : b.id}</Row>
            <Row label={t("wb.col.date")}>{dates.day(b.date) ?? "—"}</Row>
            <Row label={t("pw.col.lines")}>{f.lineNet === null ? <span className="text-muted-foreground">{t("wb.hidden")}</span> : `${money(f.lineNet)} · ${f.lines}`}</Row>
            <Row label={t("pw.col.reported")}>{b.netHidden ? <span className="text-muted-foreground">{t("wb.hidden")}</span> : money(b.reportedNet)}</Row>
            <Row label="bank_reference">{b.bankReference ?? "—"}</Row>
          </Side>
          <Side title={t("pw.side.bank")}>
            {f.deposit ? (
              <>
                <Row label="reference">{record(PAY_TYPES.bank, f.deposit.key, f.deposit.reference)}</Row>
                <Row label={t("wb.col.date")}>{dates.day(f.deposit.date) ?? "—"}</Row>
                <Row label="credit">{f.deposit.amountHidden ? <span className="text-muted-foreground">{t("wb.hidden")}</span> : money(f.deposit.credit)}</Row>
                <Row label="description">{f.deposit.description}</Row>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t("pw.side.noDeposit")}</p>
            )}
            <Row label={t("pw.col.feeJournal")}>{f.feeJournal?.name ?? "—"}</Row>
          </Side>
        </div>
      </>
    )
  }
  const p: Payment = f.payment
  return (
    <>
      <p className="text-sm">{why(f)}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Side title={p.provider}>
          <Row label={t("pw.col.transaction")}>{record(p.provider === "Paymob" ? PAY_TYPES.paymob : PAY_TYPES.kashier, p.id, p.id)}</Row>
          <Row label={t("pw.col.type")}>{t(`pw.kind.${p.kind}` as const)}</Row>
          <Row label={t("wb.col.date")}>
            <span title={p.at}>{dates.day(p.day) ?? "—"}</span>
          </Row>
          <Row label={t("pw.col.gross")}>{money(p.gross)} EGP</Row>
          <Row label={t("pw.col.fee")}>
            {p.feeHidden ? <span className="text-muted-foreground">{t("wb.hidden")}</span> : `${money(p.fee)} + ${money(p.vat)} ${t("pw.col.vat")}`}
            {f.expected ? <span className="text-muted-foreground"> · {t("pw.detail.contract", { fee: money(f.expected.fee), vat: money(f.expected.vat) })}</span> : null}
          </Row>
          <Row label={t("pw.col.instrument")}>{p.instrumentHidden ? <span className="text-muted-foreground">{t("wb.hidden")}</span> : (p.instrument ?? "—")}</Row>
          <Row label={t("pw.col.batch")}>{p.batch ?? "—"}</Row>
        </Side>
        <Side title={t("wb.side.erp")}>
          <Row label={t("pw.col.storeOrder")}>{f.store ? `${f.store.name} → ${f.store.reference ?? t("wb.noReference")}` : p.orderRef}</Row>
          <Row label="Sales Invoice">{f.invoice ? `${f.invoice.name} · ${money(f.invoice.outstanding)} ${t("pw.detail.owed")}` : "—"}</Row>
          <Row label={t("pw.col.erp")}>{f.erp ?? "—"}</Row>
          {f.duplicateOf ? <Row label={t("pw.detail.first")}>{f.duplicateOf}</Row> : null}
        </Side>
      </div>
    </>
  )
}

